/**
 * BaselineGateOverlay — Full-screen overlay shown when user tries to access
 * a feature that requires baseline completion (duels, leaderboards).
 *
 * V1 Invariant #4: Baseline calibration is required for ranked participation.
 */

import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { Shield, ArrowRight } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import BaselineProgress from '@/components/BaselineProgress';
import type { BaselineStatus } from '@/lib/baselineGate';

interface Props {
  status: BaselineStatus;
  feature: 'duels' | 'leaderboard';
}

export default function BaselineGateOverlay({ status, feature }: Props) {
  const { theme } = useTheme();
  const router = useRouter();

  const featureLabel = feature === 'duels' ? 'Compete' : 'Leaderboards';

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.content}>
        <Shield size={48} color={theme.colors.accent} />

        <Text style={[styles.title, { color: theme.colors.text }]}>
          Complete Your Baseline
        </Text>

        <Text style={[styles.body, { color: theme.colors.textSecondary }]}>
          {featureLabel} require all 5 foundational lifts to be logged.
          This ensures fair competition for everyone.
        </Text>

        <View style={styles.progress}>
          <BaselineProgress status={status} />
        </View>

        <TouchableOpacity
          style={[styles.logButton, { backgroundColor: theme.colors.accent }]}
          onPress={() => router.push({ pathname: '/(tabs)/record', params: { openPicker: '1' } })}>
          <Text style={[styles.logButtonText, { color: theme.colors.accentForeground }]}>Log Missing Core Lifts</Text>
          <ArrowRight size={16} color={theme.colors.accentForeground} />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    padding: 24,
  },
  content: {
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  body: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
  progress: {
    width: '100%',
    paddingHorizontal: 12,
    marginTop: 8,
  },
  logButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 2,
    marginTop: 8,
  },
  logButtonText: {
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
});
